import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { authApi, gamificationApi, panSpiritApi, fitnessApi, pathwayApi, lifeGoalsApi, mbtiApi } from '../api';
import PersonalModel from './PersonalModel';

interface Props {
  onOpenMBTI: () => void;
}

interface SanStats {
  san_value: number;
  san_max: number;
  san_status: string;
  san_status_icon: string;
  spirituality: number;
  spirituality_max: number;
  spirit_level: number;
  spirit_progress: number;
  spirit_next_level: number;
  total_diaries: number;
  mood_counts: Record<string, number>;
  type_counts: Record<string, number>;
  san_updated_at: string | null;
}

interface UserStats {
  total_exp: number;
  user_level: number;
  total_people: number;
  total_diaries: number;
  total_analyses: number;
  total_relationships: number;
  login_streak: number;
  level_progress: number;
  exp_to_next_level: number;
}

interface AnchorInfo {
  anchor_points: number;
  username: string;
  nickname: string;
}

const MyDashboard: React.FC<Props> = ({ onOpenMBTI }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sanStats, setSanStats] = useState<SanStats | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [anchorInfo, setAnchorInfo] = useState<AnchorInfo | null>(null);
  const [fitnessSummary, setFitnessSummary] = useState<any>(null);
  const [pathwayDashboard, setPathwayDashboard] = useState<any>(null);
  const [lifeGoals, setLifeGoals] = useState<any[]>([]);
  const [mbtiScores, setMbtiScores] = useState<{ ei: number; sn: number; tf: number; jp: number } | undefined>(undefined);
  const [subscribedPathways, setSubscribedPathways] = useState<any[]>([]);
  const [dimensionScores, setDimensionScores] = useState<Record<string, number> | undefined>(undefined);

  const loadData = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      authApi.getSanStats(),
      gamificationApi.getStats(),
      panSpiritApi.getMyAnchor(),
      fitnessApi.getSummary(),
      pathwayApi.getDashboard(),
      lifeGoalsApi.getAll('active'),
      mbtiApi.getSelfLatestResult(),
      pathwayApi.getSubscriptions(),
      gamificationApi.getWeeklyMission(),
    ]);

    if (results[0].status === 'fulfilled') setSanStats((results[0].value as any).data);
    if (results[1].status === 'fulfilled') setUserStats((results[1].value as any).data);
    if (results[2].status === 'fulfilled') setAnchorInfo((results[2].value as any).data);
    if (results[3].status === 'fulfilled') setFitnessSummary((results[3].value as any).data);
    if (results[4].status === 'fulfilled') setPathwayDashboard((results[4].value as any).data);
    if (results[5].status === 'fulfilled') setLifeGoals((results[5].value as any).data || []);
    if (results[6].status === 'fulfilled') {
      const mbtiData = (results[6].value as any).data;
      if (mbtiData && mbtiData.ei_score != null) {
        setMbtiScores({ ei: mbtiData.ei_score, sn: mbtiData.sn_score, tf: mbtiData.tf_score, jp: mbtiData.jp_score });
      }
    }
    if (results[7].status === 'fulfilled') setSubscribedPathways((results[7].value as any).data || []);
    if (results[8].status === 'fulfilled') {
      const missionData = (results[8].value as any).data;
      const scores: Record<string, number> = {};
      (missionData?.groups || []).forEach((group: any) => {
        if (group?.key && typeof group.score === 'number') {
          scores[group.key] = group.score;
        }
      });
      if (Object.keys(scores).length > 0) setDimensionScores(scores);
    }

    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const mbti = user?.mbti_type?.toUpperCase() || '';
  const sequenceLevel = userStats ? Math.max(1, 10 - userStats.user_level) : 9;
  const nickname = user?.nickname || user?.username || '未知';

  // Health & Tech computed values
  const healthLevel = fitnessSummary?.stats?.current_streak ?? fitnessSummary?.current_streak ?? 0;
  const healthHp = fitnessSummary?.hp?.hp ?? 0;
  const techProgress = pathwayDashboard?.user_progress?.[0];
  const techSequence = techProgress ? techProgress.current_sequence : 10;
  const techExp = techProgress ? techProgress.total_exp : 0;

  // Mood stats
  const topMoods = sanStats?.mood_counts
    ? Object.entries(sanStats.mood_counts).sort((a, b) => b[1] - a[1]).slice(0, 5)
    : [];
  const totalMoodCount = topMoods.reduce((s, [, c]) => s + c, 0);

  // Diary type labels
  const TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
    note: { label: '记事', icon: '📝', color: '#64748b' },
    mood: { label: '情绪', icon: '🎭', color: '#a78bfa' },
    grudge: { label: '记仇', icon: '😤', color: '#ef4444' },
    gratitude: { label: '记恩', icon: '🙏', color: '#10b981' },
  };
  const diaryTypes = sanStats?.type_counts
    ? Object.entries(sanStats.type_counts).map(([k, v]) => ({
        key: k, count: v,
        ...(TYPE_LABELS[k] || { label: k, icon: '📄', color: '#64748b' }),
      }))
    : [];

  return (
    <div className="my-root">
      {/* Content：六芒星模块直接在最上方 */}
      <main className="my-main">
        {loading ? (
          <div className="my-loading"><div className="my-spinner" /><p>加载中...</p></div>
        ) : (
          <>
            {/* ===== 游戏化个人模型 ===== */}
            <PersonalModel
              sanValue={sanStats?.san_value ?? 0}
              sanMax={sanStats?.san_max ?? 100}
              sanStatus={sanStats?.san_status ?? '理智稳定'}
              sanStatusIcon={sanStats?.san_status_icon ?? '🧠'}
              spirituality={sanStats?.spirituality ?? 0}
              spiritualityMax={sanStats?.spirituality_max ?? 1000}
              spiritLevel={sanStats?.spirit_level ?? 0}
              spiritProgress={sanStats?.spirit_progress ?? 0}
              techExp={techExp}
              techSequence={techSequence}
              healthHp={healthHp}
              healthStreak={healthLevel}
              anchorPoints={anchorInfo?.anchor_points ?? 0}
              nickname={nickname}
              mbtiType={mbti}
              sequenceLevel={sequenceLevel}
              mbtiScores={mbtiScores}
              dimensionScores={dimensionScores}
            />

            {/* ===== MBTI 测试入口 ===== */}
            <div className="my-section">
              <button className="my-mbti-entry" onClick={onOpenMBTI}>
                <span className="my-mbti-entry-icon">🧠</span>
                <div className="my-mbti-entry-info">
                  <div className="my-mbti-entry-title">MBTI 人格测试</div>
                  <div className="my-mbti-entry-sub">{mbti ? `当前类型：${mbti}　点击重新测试` : '点击开始测试'}</div>
                </div>
                <span className="my-mbti-entry-arrow">›</span>
              </button>
            </div>

            {/* ===== 数据总览 ===== */}
            <div className="my-section">
              <h3 className="my-section-title">📊 数据总览</h3>
              <div className="my-overview-grid">
                <div className="my-overview-item"><span className="my-ov-num">{userStats?.total_diaries ?? sanStats?.total_diaries ?? 0}</span><span className="my-ov-label">📜 日记</span></div>
                <div className="my-overview-item"><span className="my-ov-num">{userStats?.total_people ?? 0}</span><span className="my-ov-label">👥 人物</span></div>
                <div className="my-overview-item"><span className="my-ov-num">{userStats?.total_relationships ?? 0}</span><span className="my-ov-label">🔗 关系</span></div>
                <div className="my-overview-item"><span className="my-ov-num">{userStats?.login_streak ?? 0}</span><span className="my-ov-label">🔥 连续登录</span></div>
                <div className="my-overview-item"><span className="my-ov-num">{lifeGoals.length}</span><span className="my-ov-label">🎯 人生目标</span></div>
                <div className="my-overview-item"><span className="my-ov-num">{userStats?.total_analyses ?? 0}</span><span className="my-ov-label">🔍 分析</span></div>
              </div>
            </div>

            {/* ===== 我的神之途径 ===== */}
            {subscribedPathways.length > 0 && (
              <div className="my-section">
                <h3 className="my-section-title">⚔️ 我的神之途径</h3>
                <div className="my-pathway-list">
                  {subscribedPathways.map((sub: any) => {
                    const weeklyPct = sub.weekly_total > 0
                      ? Math.round((sub.weekly_completed / sub.weekly_total) * 100)
                      : 0;
                    const seqProgress = ((10 - sub.current_sequence) / 9) * 100;
                    return (
                      <div key={sub.pathway_id} className="my-pathway-item" style={{ '--pw-color': sub.color } as React.CSSProperties}>
                        <div className="my-pathway-icon">{sub.icon}</div>
                        <div className="my-pathway-info">
                          <div className="my-pathway-name">{sub.name}</div>
                          <div className="my-pathway-meta">
                            <span className="my-pathway-seq">序列 {sub.current_sequence}</span>
                            <span className="my-pathway-diff">{sub.difficulty}</span>
                            <span className="my-pathway-exp">⚡ {sub.total_exp} EXP</span>
                          </div>
                          <div className="my-pathway-bars">
                            <div className="my-pathway-bar-row">
                              <span className="my-pathway-bar-label">进度</span>
                              <div className="my-pathway-bar-track">
                                <div className="my-pathway-bar-fill" style={{ width: `${seqProgress}%`, background: sub.color }} />
                              </div>
                            </div>
                            {sub.weekly_total > 0 && (
                              <div className="my-pathway-bar-row">
                                <span className="my-pathway-bar-label">本周</span>
                                <div className="my-pathway-bar-track">
                                  <div className="my-pathway-bar-fill" style={{ width: `${weeklyPct}%`, background: '#00b894' }} />
                                </div>
                                <span className="my-pathway-weekly-count">{sub.weekly_completed}/{sub.weekly_total}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ===== 日记类型分布 ===== */}
            {diaryTypes.length > 0 && (
              <div className="my-section">
                <h3 className="my-section-title">📝 日记类型分布</h3>
                <div className="my-type-list">
                  {diaryTypes.map(t => {
                    const total = diaryTypes.reduce((s, x) => s + x.count, 0);
                    const pct = total > 0 ? (t.count / total * 100) : 0;
                    return (
                      <div key={t.key} className="my-type-row">
                        <span className="my-type-icon">{t.icon}</span>
                        <span className="my-type-name">{t.label}</span>
                        <div className="my-type-bar-track"><div className="my-type-bar-fill" style={{ width: `${pct}%`, background: t.color }} /></div>
                        <span className="my-type-count">{t.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ===== 心情统计 ===== */}
            {topMoods.length > 0 && (
              <div className="my-section">
                <h3 className="my-section-title">😊 心情统计 Top 5</h3>
                <div className="my-mood-grid">
                  {topMoods.map(([emoji, count]) => {
                    const pct = totalMoodCount > 0 ? (count / totalMoodCount * 100) : 0;
                    return (
                      <div key={emoji} className="my-mood-item">
                        <span className="my-mood-emoji">{emoji}</span>
                        <div className="my-mood-bar-track"><div className="my-mood-bar-fill" style={{ width: `${pct}%` }} /></div>
                        <span className="my-mood-count">{count}次</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ===== 人生目标 ===== */}
            {lifeGoals.length > 0 && (
              <div className="my-section">
                <h3 className="my-section-title">🎯 进行中的人生目标</h3>
                <div className="my-goals-list">
                  {lifeGoals.slice(0, 5).map((goal: any) => (
                    <div key={goal.id} className="my-goal-item">
                      <span className="my-goal-icon">{goal.icon || '🎯'}</span>
                      <div className="my-goal-info">
                        <div className="my-goal-title">{goal.title}</div>
                        <div className="my-goal-bar-track"><div className="my-goal-bar-fill" style={{ width: `${goal.progress || 0}%` }} /></div>
                      </div>
                      <span className="my-goal-pct">{goal.progress || 0}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <style>{`
        .my-root {
          min-height: 100vh;
          background-color: #030712;
          background-image: radial-gradient(ellipse at 50% 0%, #1e1b4b 0%, #030712 50%);
          color: #f8fafc; font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }
        .my-main {
          padding: calc(16px + var(--status-bar-height, 28px)) 16px 40px;
        }
        .my-loading { display: flex; flex-direction: column; align-items: center; padding: 80px 20px; gap: 16px; }
        .my-spinner { width: 32px; height: 32px; border: 3px solid rgba(51,65,85,0.5); border-top-color: #8b5cf6; border-radius: 50%; animation: mySpin 0.8s linear infinite; }
        @keyframes mySpin { to { transform: rotate(360deg); } }
        .my-loading p { color: rgba(148,163,184,0.6); font-size: 13px; }

        /* Tri Grid and Anchor Card replaced by PersonalModel component */

        /* Section */
        .my-section { margin-bottom: 20px; }
        .my-section-title { font-size: 15px; font-weight: 700; color: rgba(226,232,240,0.9); margin: 0 0 12px; padding-left: 2px; }

        /* MBTI Entry */
        .my-mbti-entry {
          width: 100%; display: flex; align-items: center; gap: 14px;
          background: rgba(139,92,246,0.1); border: 1px solid rgba(139,92,246,0.25);
          border-radius: 14px; padding: 14px 16px; cursor: pointer;
          text-align: left; color: inherit; transition: background 0.2s;
        }
        .my-mbti-entry:active { background: rgba(139,92,246,0.2); }
        .my-mbti-entry-icon { font-size: 28px; flex-shrink: 0; }
        .my-mbti-entry-info { flex: 1; min-width: 0; }
        .my-mbti-entry-title { font-size: 14px; font-weight: 700; color: rgba(226,232,240,0.95); }
        .my-mbti-entry-sub { font-size: 11px; color: rgba(139,92,246,0.9); margin-top: 2px; }
        .my-mbti-entry-arrow { font-size: 20px; color: rgba(139,92,246,0.6); flex-shrink: 0; }

        /* Overview */
        .my-overview-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        .my-overview-item {
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.05);
          border-radius: 12px; padding: 14px 8px;
          display: flex; flex-direction: column; align-items: center; gap: 4px;
        }
        .my-ov-num { font-size: 22px; font-weight: 800; color: white; }
        .my-ov-label { font-size: 10px; color: rgba(148,163,184,0.6); }

        /* Type Distribution */
        .my-type-list { background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.05); border-radius: 14px; padding: 14px; }
        .my-type-row { display: flex; align-items: center; gap: 8px; padding: 8px 0; }
        .my-type-row + .my-type-row { border-top: 1px solid rgba(51,65,85,0.2); }
        .my-type-icon { font-size: 16px; flex-shrink: 0; }
        .my-type-name { font-size: 12px; color: rgba(226,232,240,0.8); min-width: 36px; font-weight: 600; }
        .my-type-bar-track { flex: 1; height: 6px; background: rgba(30,41,59,0.8); border-radius: 3px; overflow: hidden; }
        .my-type-bar-fill { height: 100%; border-radius: 3px; transition: width 0.5s; }
        .my-type-count { font-size: 12px; font-weight: 700; color: rgba(226,232,240,0.7); min-width: 24px; text-align: right; }

        /* Mood */
        .my-mood-grid { background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.05); border-radius: 14px; padding: 14px; }
        .my-mood-item { display: flex; align-items: center; gap: 10px; padding: 8px 0; }
        .my-mood-item + .my-mood-item { border-top: 1px solid rgba(51,65,85,0.2); }
        .my-mood-emoji { font-size: 20px; flex-shrink: 0; }
        .my-mood-bar-track { flex: 1; height: 6px; background: rgba(30,41,59,0.8); border-radius: 3px; overflow: hidden; }
        .my-mood-bar-fill { height: 100%; border-radius: 3px; background: linear-gradient(90deg, #8b5cf6, #a78bfa); transition: width 0.5s; }
        .my-mood-count { font-size: 12px; color: rgba(148,163,184,0.6); min-width: 36px; text-align: right; }

        /* My Pathways */
        .my-pathway-list { display: flex; flex-direction: column; gap: 10px; }
        .my-pathway-item {
          display: flex; align-items: flex-start; gap: 14px;
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.05);
          border-radius: 14px; padding: 14px 16px;
          border-left: 3px solid var(--pw-color, #6C5CE7);
        }
        .my-pathway-icon { font-size: 28px; flex-shrink: 0; margin-top: 2px; }
        .my-pathway-info { flex: 1; min-width: 0; }
        .my-pathway-name {
          font-size: 13px; font-weight: 700;
          color: rgba(226,232,240,0.95); margin-bottom: 6px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .my-pathway-meta {
          display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px;
        }
        .my-pathway-seq, .my-pathway-diff, .my-pathway-exp {
          font-size: 10px; font-weight: 700; padding: 2px 8px;
          border-radius: 20px;
        }
        .my-pathway-seq {
          background: rgba(108,92,231,0.15); color: #a29bfe;
          border: 1px solid rgba(108,92,231,0.2);
        }
        .my-pathway-diff {
          background: rgba(255,255,255,0.06); color: rgba(226,232,240,0.6);
          border: 1px solid rgba(255,255,255,0.08);
        }
        .my-pathway-exp {
          background: rgba(255,214,0,0.08); color: #ffd700;
          border: 1px solid rgba(255,214,0,0.15);
        }
        .my-pathway-bars { display: flex; flex-direction: column; gap: 6px; }
        .my-pathway-bar-row { display: flex; align-items: center; gap: 8px; }
        .my-pathway-bar-label { font-size: 10px; color: rgba(148,163,184,0.5); min-width: 24px; }
        .my-pathway-bar-track { flex: 1; height: 5px; background: rgba(30,41,59,0.8); border-radius: 3px; overflow: hidden; }
        .my-pathway-bar-fill { height: 100%; border-radius: 3px; transition: width 0.5s; }
        .my-pathway-weekly-count { font-size: 10px; color: #00b894; font-weight: 700; min-width: 28px; text-align: right; }

        /* Goals */
        .my-goals-list { display: flex; flex-direction: column; gap: 8px; }
        .my-goal-item {
          display: flex; align-items: center; gap: 10px;
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.05);
          border-radius: 12px; padding: 12px 14px;
        }
        .my-goal-icon { font-size: 20px; flex-shrink: 0; }
        .my-goal-info { flex: 1; min-width: 0; }
        .my-goal-title { font-size: 13px; font-weight: 600; color: rgba(226,232,240,0.9); margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .my-goal-bar-track { height: 4px; background: rgba(30,41,59,0.8); border-radius: 2px; overflow: hidden; }
        .my-goal-bar-fill { height: 100%; background: linear-gradient(90deg, #10b981, #34d399); border-radius: 2px; transition: width 0.5s; }
        .my-goal-pct { font-size: 12px; font-weight: 700; color: rgba(148,163,184,0.7); min-width: 32px; text-align: right; }

        @media (min-width: 768px) {
          .my-main { max-width: 600px; margin-left: auto; margin-right: auto; }
        }
      `}</style>
    </div>
  );
};

export default MyDashboard;
